import { z } from "zod";

import { TUserDataSchema, TUserToken, userSchema } from "../user";

export const authLoginRequestSchema = userSchema.pick({ email: true }).extend({
  password: z.string().min(1, "Password is required"),
  username: z.string(),
  email: z.string().min(1, "Email is required"),
});

export const loginResEntitySchema = z.object({
  internal_id: z.string(),
  slug: z.string().nullable(),
  name: z.string().nullable(),
});

export const loginRoleGroupSchema = z.object({
  id: z.number().optional(),
  internal_id: z.string().optional(),
  slug: z.string().nullable(),
  name: z.string().nullable(),
  is_primary: z.boolean().optional(),
  permissions: z.array(z.string()).nullable().optional(),
});

export type TAuthLoginResponseData = {
  user: TUserDataSchema | null;
  role_group: TLoginRoleGroupSchema[];
  entity: TLoginResEntitySchema;
  auth: TUserToken;
  redirect_url: string;
};

export type TAuthLoginRequestPayload = z.infer<typeof authLoginRequestSchema>;
export type TLoginRoleGroupSchema = z.infer<typeof loginRoleGroupSchema>;
export type TLoginResEntitySchema = z.infer<typeof loginResEntitySchema>;

export type TAuthLoginResponsePayload = {
  data: TAuthLoginResponseData;
};

export type TAuthRefreshAccessTokenResponsePayload = {
  access: string;
  refresh?:string;
};

export const resetPasswordRequestSchema = z
  .object({
    password: z
      .string()
      .min(12, "Password must be at least 12 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number")
      .regex(
        /[^A-Za-z0-9]/,
        "Password must contain at least one special character",
      ),
    confirm_password: z.string(),
    verification_key: z.string(),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: "Passwords do not match",
    path: ["confirm_password"],
  });

export type TResetPasswordRequestPayload = z.infer<
  typeof resetPasswordRequestSchema
>;

export type TResetPasswordResponsePayload = {
  status: boolean;
  message: string;
  data: Record<string, unknown>;
};

export const updatePasswordRequestSchema = z
  .object({
    password: z
      .string()
      .min(12, "Password must be at least 12 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number")
      .regex(
        /[^A-Za-z0-9]/,
        "Password must contain at least one special character",
      ),
    confirm_password: z.string(),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: "Passwords do not match",
    path: ["confirm_password"],
  });

export type TUpdatePasswordRequestPayload = z.infer<
  typeof updatePasswordRequestSchema
>;

export type TUpdatePasswordResponsePayload = {
  status: boolean;
  message: string;
  data: Record<string, unknown>;
};

// ===================== forgot_password =====================
export const forgotPasswordRequestSchema = z.object({
  email: z.string().email(),
});

export type TForgotPasswordRequestPayload = z.infer<
  typeof forgotPasswordRequestSchema
>;

export type TForgotPasswordResponsePayload = {
  status: boolean;
  message: string;
  data: Record<string, unknown>;
};
// ===================== end forgot_password =====================

// ===================== onboard_user =====================
export const onboardUserRequestSchema = z.object({
  email: z.string().email(),
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  password: z
    .string()
    .min(12, "Password must be at least 12 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(
      /[^A-Za-z0-9]/,
      "Password must contain at least one special character",
    ),
  verification_key: z.string(),
});

export type TOnboardUserRequestPayload = z.infer<
  typeof onboardUserRequestSchema
>;

export type TOnboardUserResponsePayload = {
  status: boolean;
  message: string;
  data?: Record<string, unknown>;
};
// ===================== end onboard_user =====================

// ===================== invite verification =====================
export const inviteVerificationResponseSchema = z.object({
  status: z.boolean(),
  message: z.string(),
  data: z.object({
    email: z.string(),
    first_name: z.string(),
    last_name: z.string(),
  }),
});

export type TInviteVerificationResponse = z.infer<
  typeof inviteVerificationResponseSchema
>;
// ===================== end invite verification =====================

// ===================== renew password =====================
export const renewPasswordRequestSchema = z
  .object({
    email: z.string().email("Invalid email format"),
    new_password: z
      .string()
      .min(12, "New password must be at least 12 characters")
      .regex(/[A-Z]/, "New password must contain at least one uppercase letter")
      .regex(/[a-z]/, "New password must contain at least one lowercase letter")
      .regex(/[0-9]/, "New password must contain at least one number")
      .regex(
        /[^A-Za-z0-9]/,
        "New password must contain at least one special character",
      ),
    password: z.string(),
  })
  .refine((data) => data.new_password === data.password, {
    message: "Passwords do not match",
    path: ["password"],
  });

export type TRenewPasswordRequestPayload = z.infer<
  typeof renewPasswordRequestSchema
>;

export type TRenewPasswordResponsePayload = {
  status: boolean;
  message: string;
  data: Record<string, unknown>;
};
// ===================== end renew password =====================
